import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import Dialog from '@mui/material/Dialog';
import FormControl from '@mui/material/FormControl';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from 'components/LinearProgress';
import getCurrentId from 'helpers/getCurrentId';
import { createEntity, createAnswer } from 'actions/questions_advices';
import { getDailyQuest } from 'actions/gamification';
import { MAX_MESSAGE_LIMIT } from 'variables/main';

const DialogContentWrapper = styled(DialogContent)(({ theme, center }) => ({
  padding: 30,
  paddingBottom: 30,
  textAlign: center ? 'center' : 'left',
  [theme.breakpoints.down('sm')]: {
    paddingBottom: 20
  }
}));

const DialogTitleWrapper = styled(DialogTitle)(({ center }) => ({
  padding: 30,
  paddingBottom: 0,
  marginBottom: 20,
  textAlign: center ? 'center' : 'left'
}));

const DialogActionsWrapper = styled(DialogActions)(({ theme, center }) => ({
  padding: 30,
  paddingTop: 20,
  justifyContent: center ? 'center' : 'flex-end',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'flex-start',
    paddingTop: 0,
    '& button': {
      marginBottom: 0,
      margin: '0 auto',
      marginTop: 15
    }
  }
}));

const FormWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 30,
  alignItems: 'end',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 10
  }
}));

const FieldHeadline = styled(Typography)(({ theme }) => ({
  marginBottom: 10,
  fontSize: 16,
  [theme.breakpoints.down('sm')]: {
    marginBottom: 3,
    lineHeight: '24px'
  }
}));

const StyledMultiline = styled(TextField)(() => ({
  '& textarea': {
    padding: 0
  }
}));

const FlexMessage = styled('div')(({ theme }) => ({
  flex: 2,
  [theme.breakpoints.down('sm')]: {
    width: '100%'
  }
}));

const ButtonStyled = styled(Button)(({ theme, errored }) => ({
  marginBottom: errored === 'true' ? 18 : 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
    width: '100%',
    marginBottom: 15
  }
}));

const AscQuestionDialog = ({ open, type, entity, texts, activeQuestion, handleClose }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const t = useTranslate('Questions');

  const [professionId] = React.useState(getCurrentId(location));
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [errors, setErrors] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const isMobile = useSelector((state) => state.app.isMobile);

  const handleSubmit = React.useCallback(async () => {
    setErrors([]);

    if (!message) {
      setErrors((prev) => [...prev, 'message']);
    }

    if (type === 'advices' && !title) {
      setErrors((prev) => [...prev, 'title']);
    }

    if (!message) {
      return;
    }

    if (type === 'advices' && !title) {
      return;
    }

    let isSuccess = false;

    setLoading(true);

    const body = {
      text: message
    };

    if (title) {
      body.title = title;
    }

    switch (type) {
      case 'answer':
        const answered = await dispatch(createAnswer(activeQuestion, body, entity));
        isSuccess = !(answered instanceof Error);
        break;
      default:
        const advised = await dispatch(createEntity(professionId, body, entity));
        isSuccess = !(advised instanceof Error);
        break;
    }

    await getDailyQuest()(dispatch);

    setLoading(false);

    if (isSuccess) {
      setSuccess(true);
    }
  }, [message, dispatch, professionId, type, title, activeQuestion, entity]);

  const handleCloseDialog = React.useCallback(() => {
    handleClose();
    setMessage('');
    setTitle('');
    setSuccess(false);
    setErrors([]);
  }, [handleClose]);

  const isAdvices = React.useMemo(() => type === 'advices', [type]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth={true}
        maxWidth={'lg'}
        scroll={'body'}>
        <DialogContentWrapper>
          <FormWrapper>
            <FlexMessage>
              <FieldHeadline>{texts.title}</FieldHeadline>
              {isAdvices ? (
                <Box sx={{ mb: isMobile ? 0 : 2 }}>
                  <StyledMultiline
                    name="title"
                    placeholder={texts.titlePlaceholder}
                    fullWidth={true}
                    autoFocus={true}
                    value={title}
                    error={errors.includes('title')}
                    onChange={(e) => setTitle(e.target.value)}
                    slotProps={{
                      htmlInput: { maxLength: MAX_MESSAGE_LIMIT, 'aria-label': texts.placeholder }
                    }}
                  />
                  {errors.includes('title') ? (
                    <FormControl variant="standard" error={true}>
                      <FormHelperText>{t('RequiredError')}</FormHelperText>
                    </FormControl>
                  ) : null}
                  <FieldHeadline sx={{ mt: isMobile ? 2 : 4 }}>{texts.subtitle}</FieldHeadline>
                </Box>
              ) : null}
              <Box>
                <StyledMultiline
                  name="message"
                  placeholder={texts.placeholder}
                  rows={4}
                  fullWidth={true}
                  autoFocus={!isAdvices}
                  multiline={true}
                  value={message}
                  error={errors.includes('message')}
                  onChange={(e) => setMessage(e.target.value)}
                  slotProps={{
                    htmlInput: { maxLength: MAX_MESSAGE_LIMIT, 'aria-label': texts.placeholder }
                  }}
                />
                {errors.includes('message') ? (
                  <FormControl variant="standard" error={true}>
                    <FormHelperText>{t('RequiredError')}</FormHelperText>
                  </FormControl>
                ) : null}
              </Box>
            </FlexMessage>
          </FormWrapper>
          {loading ? (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />{' '}
            </Box>
          ) : null}
        </DialogContentWrapper>

        <DialogActionsWrapper>
          <ButtonStyled
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            aria-label={t('Send')}
            errored={errors.length > 0 ? 'true' : 'false'}>
            {texts?.send}
          </ButtonStyled>
        </DialogActionsWrapper>
      </Dialog>

      <Dialog open={success} onClose={handleCloseDialog}>
        <DialogTitleWrapper center="true">{texts.successTitle}</DialogTitleWrapper>
        <DialogContentWrapper center="true">
          <Typography>{t('SendSuccessText')}</Typography>
        </DialogContentWrapper>
        <DialogActionsWrapper center="true">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCloseDialog}
            aria-label={t('Close')}>
            {t('Close')}
          </Button>
        </DialogActionsWrapper>
      </Dialog>
    </>
  );
};

export default AscQuestionDialog;
